<template>
  <form class="search-form" @submit.prevent="onSubmit">
    <div class="row mb-3">
      <div class="col-12">
        <div class="d-flex align-items-center gap-2">
          <div class="position-relative material-number-field flex-grow-1">
            <i class="pi pi-search material-search-icon"></i>
            <input v-model="formData.material" type="text" class="form-control"
              placeholder="Search Material / Description / Keyword" />
            <i v-if="formData.material" class="pi pi-times material-clear-icon" @click="formData.material = ''"></i>
          </div>
          <div class="position-relative">
            <ButtonPrime v-tooltip.bottom="allFieldsVisible ? 'Hide All Fields' : 'Show All Fields'"
              :icon="allFieldsVisible ? 'pi pi-eye' : 'pi pi-eye-slash'" size="normal" raised rounded
              :severity="allFieldsVisible ? 'primary' : 'secondary'"
              :aria-label="allFieldsVisible ? 'Hide All Fields' : 'Show All Fields'" @click="toggleFieldFilter" />
          </div>
        </div>
      </div>
    </div>

    <!-- Basic Data -->
    <div v-if="isGroupVisible('Basic Data')" class="form-section mb-2">
      <div class="form-section-title">Basic Data</div>
      <div class="row">
        <div v-if="visibleFields.includes('item_name')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getItemNameDisplayValue()" :suggestions="itemNameSuggestions"
            placeholder="Enter Item Name (INC)" fluid @update:model-value="onItemNameInput"
            @complete="searchItemNames($event.query)" @item-select="onItemNameSelect" />
        </div>
        <div v-if="visibleFields.includes('item_type')" class="col-md-4 mb-3">
          <select v-model="formData.item_type" class="form-select">
            <option value="">Select Item Type</option>
            <option v-for="opt in itemTypeOptions" :key="opt.id" :value="opt.code">{{ opt.code }}</option>
          </select>
        </div>
        <div v-if="visibleFields.includes('material_type')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getMaterialTypeDisplayValue()" :suggestions="materialTypeSuggestions"
            placeholder="Enter Material Type" fluid @update:model-value="onMaterialTypeInput"
            @complete="searchMaterialTypes($event.query)" @item-select="onMaterialTypeSelect" />
        </div>
        <div v-if="visibleFields.includes('material_group')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getMaterialGroupDisplayValue()" :suggestions="materialGroupSuggestions"
            placeholder="Enter Material Group" fluid @update:model-value="onMaterialGroupInput"
            @complete="searchMaterialGroups($event.query)" @item-select="onMaterialGroupSelect" />
        </div>
        <div v-if="visibleFields.includes('uom')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getUomDisplayValue()" :suggestions="uomSuggestions" placeholder="Enter UOM" fluid
            @update:model-value="onUomInput" @complete="searchUoms($event.query)" @item-select="onUomSelect" />
        </div>
        <div v-if="visibleFields.includes('valuation_class')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getValuationClassDisplayValue()" :suggestions="valuationClassSuggestions"
            placeholder="Select Valuation Class" fluid @update:model-value="onValuationClassInput"
            @complete="searchValuationClasses($event.query)" @item-select="onValuationClassSelect" />
        </div>
        <div v-if="visibleFields.includes('group_class')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getGroupClassDisplayValue()" :suggestions="groupClassSuggestions"
            placeholder="Enter Group Class" fluid @update:model-value="onGroupClassInput"
            @complete="searchGroupClasses($event.query)" @item-select="onGroupClassSelect" />
        </div>
        <div v-if="visibleFields.includes('old_material_number')" class="col-md-4 mb-3">
          <inputText v-model="formData.old_material_number" type="text" class="form-control"
            placeholder="Input Old Material Number" />
        </div>
        <div v-if="companyCode === 'MIND-ID'" class="col-md-4 mb-3">
          <AutoComplete :model-value="getClientDisplayValue()" :suggestions="clientSuggestions"
            placeholder="Select Client" fluid @update:model-value="onClientInput"
            @complete="searchClients($event.query)" @item-select="onClientSelect" />
        </div>
      </div>
    </div>

    <!-- Plant -->
    <div v-if="isGroupVisible('Plant')" class="form-section mb-2">
      <div class="form-section-title">Plant</div>
      <div class="row">
        <div v-if="visibleFields.includes('plant')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getPlantDisplayValue()" :suggestions="plantSuggestions" placeholder="Select Plant"
            fluid @update:model-value="onPlantInput" @complete="searchPlants($event.query)"
            @item-select="onPlantSelect" />
        </div>
        <div v-if="visibleFields.includes('location')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getLocationDisplayValue()" :suggestions="locationSuggestions"
            placeholder="Select Location" fluid @update:model-value="onLocationInput"
            @complete="searchLocations($event.query)" @item-select="onLocationSelect" />
        </div>
        <div v-if="visibleFields.includes('mind_id_number')" class="col-md-4 mb-3">
          <input v-model="formData.mind_id_number" type="text" class="form-control" placeholder="Enter MIND ID Number" />
        </div>
        <div v-if="companyCode === 'ANTAM' && visibleFields.includes('mrp_group')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getMrpGroupDisplayValue()" :suggestions="mrpGroupSuggestions"
            placeholder="Select MRP Group" fluid @update:model-value="onMrpGroupInput"
            @complete="searchMrpGroups($event.query)" @item-select="onMrpGroupSelect" />
        </div>
        <div v-if="companyCode === 'ANTAM' && visibleFields.includes('mrp_type')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getMrpTypeDisplayValue()" :suggestions="mrpTypeSuggestions"
            placeholder="Select MRP Type" fluid @update:model-value="onMrpTypeInput"
            @complete="searchMrpTypes($event.query)" @item-select="onMrpTypeSelect" />
        </div>
        <div v-if="companyCode === 'ANTAM' && visibleFields.includes('material_freight_group')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getMaterialFreightGroupDisplayValue()" :suggestions="materialFreightGroupSuggestions"
            placeholder="Select Material Freight Group" fluid @update:model-value="onMaterialFreightGroupInput"
            @complete="searchMaterialFreightGroups($event.query)" @item-select="onMaterialFreightGroupSelect" />
        </div>
      </div>
    </div>

    <!-- Manufacture & Equipment -->
    <div v-if="isGroupVisible('Manufacture & Equipment')" class="form-section mb-2">
      <div class="form-section-title">Manufacture &amp; Equipment</div>
      <div class="row">
        <div v-if="visibleFields.includes('manufacture_code')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getManufactureCodeDisplayValue()" :suggestions="manufactureCodeSuggestions"
            placeholder="Select Manufacture Code" fluid @update:model-value="onManufactureCodeInput"
            @complete="searchManufactureCodes($event.query)" @item-select="onManufactureCodeSelect" />
        </div>
        <div v-if="visibleFields.includes('manufacture_ref')" class="col-md-4 mb-3">
          <input v-model="formData.manufacture_ref" type="text" class="form-control"
            placeholder="Enter Manufacture Ref" />
        </div>
        <div v-if="visibleFields.includes('equipment')" class="col-md-4 mb-3">
          <AutoComplete :model-value="getEquipmentDisplayValue()" :suggestions="equipmentSuggestions"
            placeholder="Select Equipment" fluid @update:model-value="onEquipmentInput"
            @complete="searchEquipments($event.query)" @item-select="onEquipmentSelect" />
        </div>
      </div>
    </div>

    <!-- Other -->
    <div v-if="isGroupVisible('Other')" class="form-section mb-2">
      <div class="form-section-title">Other</div>
      <div class="row">
        <div v-if="visibleFields.includes('mcr_number')" class="col-md-4 mb-3">
          <input v-model="formData.mcr_number" type="text" class="form-control" placeholder="Enter MCR Number" />
        </div>
        <div v-if="visibleFields.includes('created_at')" class="col-md-4 mb-3">
          <input v-model="formData.created_at" type="date" class="form-control" placeholder="Select Created At" />
        </div>
      </div>
    </div>

    <div class="row justify-content-center">
      <div class="col">
        <button type="submit" class="btn btn-primary btn-sm w-100" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ loading ? 'Searching...' : 'Search' }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import manufacturerService from '@/services/generalMasterData/manufacturerService';
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import standarItemNameService from '@/services/dictionary/standarItemNameService';
import natoGroupService from "@/services/dictionary/natoGroupService";
import clientService from "@/services/generalMasterData/clientService";
import api from "@/helpers/api";
import { initialFormData } from './materialSearchFields';

export default {
  name: 'MaterialSearchForm',
  props: {
    // Prefill values (e.g. from URL query on the search page). Cloned on init.
    initialData: {
      type: Object,
      default: () => ({}),
    },
    // Drives the Search button spinner/disabled state from the parent.
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  data() {
    return {
      formData: { ...initialFormData, ...(this.initialData || {}) },
      fieldGroups: [
        {
          title: 'Basic Data',
          fields: [
            { key: 'item_name', label: 'Item Name' },
            { key: 'item_type', label: 'Item Type' },
            { key: 'material_type', label: 'Material Type' },
            { key: 'material_group', label: 'Material Group' },
            { key: 'uom', label: 'UOM' },
            { key: 'valuation_class', label: 'Valuation Class' },
            { key: 'group_class', label: 'Group Class' },
            { key: 'old_material_number', label: 'Old Material Number' },
          ],
        },
        {
          title: 'Plant',
          fields: [
            { key: 'plant', label: 'Plant' },
            { key: 'location', label: 'Location' },
            { key: 'mind_id_number', label: 'MIND ID Number' },
            { key: 'mrp_group', label: 'MRP Group' },
            { key: 'mrp_type', label: 'MRP Type' },
            { key: 'material_freight_group', label: 'Material Freight Group' },
          ],
        },
        {
          title: 'Manufacture & Equipment',
          fields: [
            { key: 'manufacture_code', label: 'Manufacture Code' },
            { key: 'manufacture_ref', label: 'Manufacture Ref' },
            { key: 'equipment', label: 'Equipment' },
          ],
        },
        {
          title: 'Other',
          fields: [
            { key: 'mcr_number', label: 'MCR' },
            { key: 'created_at', label: 'Created At' },
          ],
        },
      ],
      visibleFields: [],

      // Item Name autocomplete data
      itemNameSuggestions: [],
      selectedItemNameObject: null,
      allItemNameData: [],

      // Plant autocomplete data
      plantSuggestions: [],
      selectedPlantObject: null,
      allPlantData: [],

      // Material Type autocomplete data
      materialTypeSuggestions: [],
      selectedMaterialTypeObject: null,
      allMaterialTypeData: [],

      // Material Group autocomplete data
      materialGroupSuggestions: [],
      selectedMaterialGroupObject: null,
      allMaterialGroupData: [],

      // UOM autocomplete data
      uomSuggestions: [],
      selectedUomObject: null,
      allUomData: [],

      // Manufacture Code autocomplete data
      manufactureCodeSuggestions: [],
      selectedManufactureCodeObject: null,
      allManufactureCodeData: [],

      // Location autocomplete data
      locationSuggestions: [],
      selectedLocationObject: null,
      allLocationData: [],

      // Equipment autocomplete data
      equipmentSuggestions: [],
      selectedEquipmentObject: null,
      allEquipmentData: [],

      // Item Type options
      itemTypeOptions: [],
      clientCode: "",
      companyCode: "",

      // Valuation Class autocomplete data
      valuationClassSuggestions: [],
      selectedValuationClassObject: null,
      allValuationClassData: [],

      // Group Class (NATO) autocomplete data
      groupClassSuggestions: [],
      selectedGroupClassObject: null,
      allGroupClassData: [],

      // Client autocomplete data (cuma tampil untuk companyCode MIND-ID)
      clientSuggestions: [],
      selectedClientObject: null,
      allClientData: [],

      // MRP Group autocomplete data (cuma tampil untuk companyCode ANTAM)
      mrpGroupSuggestions: [],
      selectedMrpGroupObject: null,
      allMrpGroupData: [],

      // MRP Type autocomplete data (cuma tampil untuk companyCode ANTAM)
      mrpTypeSuggestions: [],
      selectedMrpTypeObject: null,
      allMrpTypeData: [],

      // Material Freight Group autocomplete data (cuma tampil untuk companyCode ANTAM)
      materialFreightGroupSuggestions: [],
      selectedMaterialFreightGroupObject: null,
      allMaterialFreightGroupData: [],
    };
  },
  computed: {
    // Total semua field yang bisa difilter
    totalFieldCount() {
      return this.fieldGroups.reduce((sum, g) => sum + g.fields.length, 0);
    },
    allFieldsVisible() {
      return this.visibleFields.length === this.totalFieldCount;
    },
  },
  watch: {
    // Reseed form ketika parent mengirim prefill baru (mis. dari URL query)
    initialData: {
      deep: true,
      handler(val) {
        this.formData = { ...initialFormData, ...(val || {}) };
        // Reset selected object agar AutoComplete menampilkan raw code
        this.resetSelectedObjects();
      },
    },
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.clientCode = userAuth?.user?.client_code || '';
    this.companyCode = userAuth?.user?.company_code || '';
    this.fetchItemTypes();
  },
  methods: {
    toggleFieldFilter() {
      if (this.allFieldsVisible) {
        this.visibleFields = [];
      } else {
        this.visibleFields = this.fieldGroups.flatMap(g => g.fields.map(f => f.key));
      }
    },

    // Cek apakah sebuah grup punya minimal 1 field yang tampil
    isGroupVisible(title) {
      const group = this.fieldGroups.find(g => g.title === title);
      if (!group) return false;
      return group.fields.some(f => this.visibleFields.includes(f.key));
    },

    // Bangun params bersih (drop nilai kosong/null) dari formData
    buildParams() {
      const params = {};
      Object.entries(this.formData).forEach(([key, value]) => {
        if (value !== '' && value != null) params[key] = value;
      });
      return params;
    },

    // Submit form -> emit cleaned params ke parent
    onSubmit() {
      const params = this.buildParams();
      this.$emit('submit', params);
    },

    // Reset semua selected object autocomplete (display jatuh ke raw value)
    resetSelectedObjects() {
      this.selectedItemNameObject = null;
      this.selectedPlantObject = null;
      this.selectedMaterialTypeObject = null;
      this.selectedMaterialGroupObject = null;
      this.selectedUomObject = null;
      this.selectedManufactureCodeObject = null;
      this.selectedLocationObject = null;
      this.selectedEquipmentObject = null;
      this.selectedValuationClassObject = null;
      this.selectedGroupClassObject = null;
      this.selectedClientObject = null;
      this.selectedMrpGroupObject = null;
      this.selectedMrpTypeObject = null;
      this.selectedMaterialFreightGroupObject = null;
    },

    // Get display value for item name autocomplete
    getItemNameDisplayValue() {
      if (this.selectedItemNameObject && this.selectedItemNameObject.label) {
        return this.selectedItemNameObject.label;
      }
      return this.formData.item_name || '';
    },

    // Search item names for autocomplete
    async searchItemNames(query) {
      if (!query || query.length < 2) {
        this.itemNameSuggestions = [];
        return;
      }

      try {
        const response = await standarItemNameService.list({ search: query, limit: 100 });
        if (response.data && response.data.data && response.data.data.data) {
          this.allItemNameData = response.data.data.data;
          this.itemNameSuggestions = response.data.data.data.map(item => item.label);
        } else {
          this.itemNameSuggestions = [];
          this.allItemNameData = [];
        }
      } catch (error) {
        console.error('Error searching item names:', error);
        this.itemNameSuggestions = [];
        this.allItemNameData = [];
      }
    },

    // Handle item name input (when user types manually)
    onItemNameInput(value) {
      const matchingItem = this.allItemNameData.find(item => item.label === value);
      if (matchingItem) {
        this.selectedItemNameObject = matchingItem;
        this.formData.item_name = matchingItem.inc; // Store the INC when selected from suggestions
      } else {
        this.selectedItemNameObject = null;
        this.formData.item_name = value;
      }
    },

    // Handle item name selection from autocomplete dropdown
    onItemNameSelect(event) {
      if (event.value) {
        const matchingItem = this.allItemNameData.find(item => item.label === event.value);
        if (matchingItem) {
          this.selectedItemNameObject = matchingItem;
          this.formData.item_name = matchingItem.inc; // Store the INC when selected
        }
      }
    },

    // Get display value for plant autocomplete
    getPlantDisplayValue() {
      // If we have a selected plant object, show its label
      if (this.selectedPlantObject && this.selectedPlantObject.label) {
        return this.selectedPlantObject.label;
      }
      // Otherwise, show the raw value from formData.plant
      return this.formData.plant || '';
    },

    // Search plants for autocomplete
    async searchPlants(query) {
      if (!query || query.length < 2) {
        this.plantSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('plant', { search: query, client: this.clientCode });
        if (response.data && response.data.data) {
          this.allPlantData = response.data.data;
          this.plantSuggestions = response.data.data.map(plant => plant.label);
        } else {
          this.plantSuggestions = [];
          this.allPlantData = [];
        }
      } catch (error) {
        console.error('Error searching plants:', error);
        this.plantSuggestions = [];
        this.allPlantData = [];
      }
    },

    // Handle plant input (when user types manually)
    onPlantInput(value) {
      // If value is one of our plant suggestions, find the matching object in allPlantData
      const matchingPlant = this.allPlantData.find(plant => plant.label === value);
      if (matchingPlant) {
        this.selectedPlantObject = matchingPlant;
        this.formData.plant = matchingPlant.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedPlantObject = null;
        this.formData.plant = value;
      }
    },

    // Handle plant selection from autocomplete dropdown
    onPlantSelect(event) {
      if (event.value) {
        // Find the matching plant in allPlantData based on the selected label
        const matchingPlant = this.allPlantData.find(plant => plant.label === event.value);
        if (matchingPlant) {
          this.selectedPlantObject = matchingPlant;
          this.formData.plant = matchingPlant.code; // Store the code when selected
        }
      }
    },

    // Get display value for material type autocomplete
    getMaterialTypeDisplayValue() {
      // If we have a selected material type object, show its label
      if (this.selectedMaterialTypeObject && this.selectedMaterialTypeObject.label) {
        return this.selectedMaterialTypeObject.label;
      }
      // Otherwise, show the raw value from formData.material_type
      return this.formData.material_type || '';
    },

    // Search material types for autocomplete
    async searchMaterialTypes(query) {
      if (!query || query.length < 2) {
        this.materialTypeSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('material-type', { search: query, limit: 100, client: this.clientCode });
        if (response.data && response.data.data && response.data.data.data) {
          this.allMaterialTypeData = response.data.data.data;
          this.materialTypeSuggestions = response.data.data.data.map(mt => mt.label);
        } else {
          this.materialTypeSuggestions = [];
          this.allMaterialTypeData = [];
        }
      } catch (error) {
        console.error('Error searching material types:', error);
        this.materialTypeSuggestions = [];
        this.allMaterialTypeData = [];
      }
    },

    // Handle material type input (when user types manually)
    onMaterialTypeInput(value) {
      // If value is one of our material type suggestions, find the matching object
      const matchingMaterialType = this.allMaterialTypeData.find(mt => mt.label === value);
      if (matchingMaterialType) {
        this.selectedMaterialTypeObject = matchingMaterialType;
        this.formData.material_type = matchingMaterialType.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedMaterialTypeObject = null;
        this.formData.material_type = value;
      }
    },

    // Handle material type selection from autocomplete dropdown
    onMaterialTypeSelect(event) {
      if (event.value) {
        // Find the matching material type in allMaterialTypeData based on the selected label
        const matchingMaterialType = this.allMaterialTypeData.find(mt => mt.label === event.value);
        if (matchingMaterialType) {
          this.selectedMaterialTypeObject = matchingMaterialType;
          this.formData.material_type = matchingMaterialType.code; // Store the code when selected
        }
      }
    },

    // Get display value for material group autocomplete
    getMaterialGroupDisplayValue() {
      // If we have a selected material group object, show its label
      if (this.selectedMaterialGroupObject && this.selectedMaterialGroupObject.label) {
        return this.selectedMaterialGroupObject.label;
      }
      // Otherwise, show the raw value from formData.material_group
      return this.formData.material_group || '';
    },

    // Search material groups for autocomplete
    async searchMaterialGroups(query) {
      if (!query || query.length < 2) {
        this.materialGroupSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('material-group', { search: query, limit: 100, client: this.clientCode });
        if (response.data && response.data.data && response.data.data.data) {
          const materialGroups = response.data.data.data.map(mg => {
            if (!mg.label) {
              mg.label = `${mg.code} - ${mg.description}`;
            }
            return mg;
          });

          // Store the full material group data for reference when selection is made
          this.allMaterialGroupData = materialGroups;
          // For suggestions, use just the label strings
          this.materialGroupSuggestions = materialGroups.map(mg => mg.label);
        } else {
          this.materialGroupSuggestions = [];
          this.allMaterialGroupData = [];
        }
      } catch (error) {
        console.error('Error searching material groups:', error);
        this.materialGroupSuggestions = [];
        this.allMaterialGroupData = [];
      }
    },

    // Handle material group input (when user types manually)
    onMaterialGroupInput(value) {
      // If value is one of our material group suggestions, find the matching object
      const matchingMaterialGroup = this.allMaterialGroupData.find(mg => mg.label === value);
      if (matchingMaterialGroup) {
        this.selectedMaterialGroupObject = matchingMaterialGroup;
        this.formData.material_group = matchingMaterialGroup.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedMaterialGroupObject = null;
        this.formData.material_group = value;
      }
    },

    // Handle material group selection from autocomplete dropdown
    onMaterialGroupSelect(event) {
      if (event.value) {
        // Find the matching material group in allMaterialGroupData based on the selected label
        const matchingMaterialGroup = this.allMaterialGroupData.find(mg => mg.label === event.value);
        if (matchingMaterialGroup) {
          this.selectedMaterialGroupObject = matchingMaterialGroup;
          this.formData.material_group = matchingMaterialGroup.code; // Store the code when selected
        }
      }
    },

    // Get display value for UOM autocomplete
    getUomDisplayValue() {
      // If we have a selected UOM object, show its label
      if (this.selectedUomObject && this.selectedUomObject.label) {
        return this.selectedUomObject.label;
      }
      // Otherwise, show the raw value from formData.uom
      return this.formData.uom || '';
    },

    // Search UOMs for autocomplete
    async searchUoms(query) {
      if (!query || query.length < 2) {
        this.uomSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('uom', { search: query, limit: 100, client: this.clientCode });
        if (response.data && response.data.data && response.data.data.data) {
          this.allUomData = response.data.data.data;
          this.uomSuggestions = response.data.data.data.map(uom => uom.label);
        } else {
          this.uomSuggestions = [];
          this.allUomData = [];
        }
      } catch (error) {
        console.error('Error searching UOMs:', error);
        this.uomSuggestions = [];
        this.allUomData = [];
      }
    },

    // Handle UOM input (when user types manually)
    onUomInput(value) {
      // If value is one of our UOM suggestions, find the matching object
      const matchingUom = this.allUomData.find(uom => uom.label === value);
      if (matchingUom) {
        this.selectedUomObject = matchingUom;
        this.formData.uom = matchingUom.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedUomObject = null;
        this.formData.uom = value;
      }
    },

    // Handle UOM selection from autocomplete dropdown
    onUomSelect(event) {
      if (event.value) {
        // Find the matching UOM in allUomData based on the selected label
        const matchingUom = this.allUomData.find(uom => uom.label === event.value);
        if (matchingUom) {
          this.selectedUomObject = matchingUom;
          this.formData.uom = matchingUom.code; // Store the code when selected
        }
      }
    },

    // Get display value for manufacture code autocomplete
    getManufactureCodeDisplayValue() {
      // If we have a selected manufacture code object, show its label
      if (this.selectedManufactureCodeObject && this.selectedManufactureCodeObject.label) {
        return this.selectedManufactureCodeObject.label;
      }
      // Otherwise, show the raw value from formData.manufacture_code
      return this.formData.manufacture_code || '';
    },

    // Search manufacture codes for autocomplete
    async searchManufactureCodes(query) {
      if (!query || query.length < 2) {
        this.manufactureCodeSuggestions = [];
        return;
      }

      try {
        const response = await manufacturerService.list({ search: query, limit: 100, client: this.clientCode });
        if (response.data && response.data.data && response.data.data.data) {
          this.allManufactureCodeData = response.data.data.data;
          this.manufactureCodeSuggestions = response.data.data.data.map(mf => mf.label);
        } else {
          this.manufactureCodeSuggestions = [];
          this.allManufactureCodeData = [];
        }
      } catch (error) {
        console.error('Error searching manufacture codes:', error);
        this.manufactureCodeSuggestions = [];
        this.allManufactureCodeData = [];
      }
    },

    // Handle manufacture code input (when user types manually)
    onManufactureCodeInput(value) {
      // If value is one of our manufacture code suggestions, find the matching object
      const matchingManufactureCode = this.allManufactureCodeData.find(mf => mf.label === value);
      if (matchingManufactureCode) {
        this.selectedManufactureCodeObject = matchingManufactureCode;
        this.formData.manufacture_code = matchingManufactureCode.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedManufactureCodeObject = null;
        this.formData.manufacture_code = value;
      }
    },

    // Handle manufacture code selection from autocomplete dropdown
    onManufactureCodeSelect(event) {
      if (event.value) {
        // Find the matching manufacture code in allManufactureCodeData based on the selected label
        const matchingManufactureCode = this.allManufactureCodeData.find(mf => mf.label === event.value);
        if (matchingManufactureCode) {
          this.selectedManufactureCodeObject = matchingManufactureCode;
          this.formData.manufacture_code = matchingManufactureCode.code; // Store the code when selected
        }
      }
    },

    // Get display value for location autocomplete
    getLocationDisplayValue() {
      // If we have a selected location object, show its label
      if (this.selectedLocationObject && this.selectedLocationObject.label) {
        return this.selectedLocationObject.label;
      }
      // Otherwise, show the raw value from formData.location
      return this.formData.location || '';
    },

    // Search locations for autocomplete
    async searchLocations(query) {
      if (!query || query.length < 2) {
        this.locationSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('storage-locations', { search: query, client: this.clientCode });
        const rows = response.data?.data || [];
        if (Array.isArray(rows) && rows.length > 0) {
          this.allLocationData = rows;
          this.locationSuggestions = rows.map(loc => loc.label);
        } else {
          this.locationSuggestions = [];
          this.allLocationData = [];
        }
      } catch (error) {
        console.error('Error searching locations:', error);
        this.locationSuggestions = [];
        this.allLocationData = [];
      }
    },

    // Handle location input (when user types manually)
    onLocationInput(value) {
      // If value is one of our location suggestions, find the matching object
      const matchingLocation = this.allLocationData.find(loc => loc.label === value);
      if (matchingLocation) {
        this.selectedLocationObject = matchingLocation;
        this.formData.location = matchingLocation.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedLocationObject = null;
        this.formData.location = value;
      }
    },

    // Handle location selection from autocomplete dropdown
    onLocationSelect(event) {
      if (event.value) {
        // Find the matching location in allLocationData based on the selected label
        const matchingLocation = this.allLocationData.find(loc => loc.label === event.value);
        if (matchingLocation) {
          this.selectedLocationObject = matchingLocation;
          this.formData.location = matchingLocation.code; // Store the code when selected
        }
      }
    },

    // Get display value for equipment autocomplete
    getEquipmentDisplayValue() {
      // If we have a selected equipment object, show its label
      if (this.selectedEquipmentObject && this.selectedEquipmentObject.label) {
        return this.selectedEquipmentObject.label;
      }
      return this.formData.equipment || '';
    },

    async searchEquipments(query) {
      if (!query || query.length < 2) {
        this.equipmentSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('equipment', { search: query, limit: 100, client: this.clientCode });
        if (response.data && response.data.data && response.data.data.data) {
          this.allEquipmentData = response.data.data.data;
          this.equipmentSuggestions = response.data.data.data.map(eq => eq.label);
        } else {
          this.equipmentSuggestions = [];
          this.allEquipmentData = [];
        }
      } catch (error) {
        console.error('Error searching equipments:', error);
        this.equipmentSuggestions = [];
        this.allEquipmentData = [];
      }
    },

    // Handle equipment input (when user types manually)
    onEquipmentInput(value) {
      // If value is one of our equipment suggestions, find the matching object
      const matchingEquipment = this.allEquipmentData.find(eq => eq.label === value);
      if (matchingEquipment) {
        this.selectedEquipmentObject = matchingEquipment;
        this.formData.equipment = matchingEquipment.code; // Store the code when selected from suggestions
      } else {
        // If user types manually, clear the selected object and store the typed value
        this.selectedEquipmentObject = null;
        this.formData.equipment = value;
      }
    },

    // Handle equipment selection from autocomplete dropdown
    onEquipmentSelect(event) {
      if (event.value) {
        // Find the matching equipment in allEquipmentData based on the selected label
        const matchingEquipment = this.allEquipmentData.find(eq => eq.label === event.value);
        if (matchingEquipment) {
          this.selectedEquipmentObject = matchingEquipment;
          this.formData.equipment = matchingEquipment.code; // Store the code when selected
        }
      }
    },

    // Fetch item type options from API
    async fetchItemTypes() {
      try {
        const response = await generalMasterDataService.list('item_type');
        if (response.data && Array.isArray(response.data.data)) {
          this.itemTypeOptions = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching item types:', error);
      }
    },

    // Get display value for Valuation Class autocomplete
    getValuationClassDisplayValue() {
      if (this.selectedValuationClassObject && this.selectedValuationClassObject.label) {
        return this.selectedValuationClassObject.label;
      }
      return this.formData.valuation_class || '';
    },

    // Search Valuation Classes for autocomplete
    async searchValuationClasses(query) {
      if (!query || query.length < 2) {
        this.valuationClassSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('valuation-classes', { search: query, limit: 100, client: this.clientCode });
        const listData = response.data && response.data.data;
        const items = Array.isArray(listData) ? listData : (listData && Array.isArray(listData.data) ? listData.data : []);

        if (items.length > 0) {
          this.allValuationClassData = items;
          this.valuationClassSuggestions = items.map(vc => vc.label);
        } else {
          this.valuationClassSuggestions = [];
          this.allValuationClassData = [];
        }
      } catch (error) {
        console.error('Error searching valuation classes:', error);
        this.valuationClassSuggestions = [];
        this.allValuationClassData = [];
      }
    },

    // Handle Valuation Class input (when user types manually)
    onValuationClassInput(value) {
      const matchingValuationClass = this.allValuationClassData.find(vc => vc.label === value);
      if (matchingValuationClass) {
        this.selectedValuationClassObject = matchingValuationClass;
        this.formData.valuation_class = matchingValuationClass.code; // Store the code when selected from suggestions
      } else {
        this.selectedValuationClassObject = null;
        this.formData.valuation_class = value;
      }
    },

    // Handle Valuation Class selection from autocomplete dropdown
    onValuationClassSelect(event) {
      if (event.value) {
        const matchingValuationClass = this.allValuationClassData.find(vc => vc.label === event.value);
        if (matchingValuationClass) {
          this.selectedValuationClassObject = matchingValuationClass;
          this.formData.valuation_class = matchingValuationClass.code; // Store the code when selected
        }
      }
    },

    // Get display value for Group Class (NATO) autocomplete
    getGroupClassDisplayValue() {
      if (this.selectedGroupClassObject && this.selectedGroupClassObject.label) {
        return this.selectedGroupClassObject.label;
      }
      return this.formData.group_class || '';
    },

    // Search Group Classes (NATO) for autocomplete
    async searchGroupClasses(query) {
      if (!query || query.length < 2) {
        this.groupClassSuggestions = [];
        return;
      }

      try {
        const response = await natoGroupService.list({ search: query, limit: 100 });
        const items = response.data?.data?.data || [];
        if (Array.isArray(items) && items.length > 0) {
          // Build label "nsc - description"; keep nsc for the value
          this.allGroupClassData = items.map(item => ({
            ...item,
            label: `${item.nsc} - ${item.description}`,
          }));
          this.groupClassSuggestions = this.allGroupClassData.map(item => item.label);
        } else {
          this.groupClassSuggestions = [];
          this.allGroupClassData = [];
        }
      } catch (error) {
        console.error('Error searching group classes:', error);
        this.groupClassSuggestions = [];
        this.allGroupClassData = [];
      }
    },

    // Handle Group Class input (when user types manually)
    onGroupClassInput(value) {
      const matchingGroupClass = this.allGroupClassData.find(item => item.label === value);
      if (matchingGroupClass) {
        this.selectedGroupClassObject = matchingGroupClass;
        this.formData.group_class = matchingGroupClass.nsc; // Store the NSC when selected from suggestions
      } else {
        this.selectedGroupClassObject = null;
        this.formData.group_class = value;
      }
    },

    // Handle Group Class selection from autocomplete dropdown
    onGroupClassSelect(event) {
      if (event.value) {
        const matchingGroupClass = this.allGroupClassData.find(item => item.label === event.value);
        if (matchingGroupClass) {
          this.selectedGroupClassObject = matchingGroupClass;
          this.formData.group_class = matchingGroupClass.nsc; // Store the NSC when selected
        }
      }
    },

    // Get display value for Client autocomplete
    getClientDisplayValue() {
      if (this.selectedClientObject && this.selectedClientObject.label) {
        return this.selectedClientObject.label;
      }
      return this.formData.client || '';
    },

    // Search Clients for autocomplete
    async searchClients(query) {
      if (!query || query.length < 2) {
        this.clientSuggestions = [];
        return;
      }

      try {
        const response = await api(this, clientService.list({ search: query, limit: 100 }), false);
        const items = response.data?.data || [];
        if (Array.isArray(items) && items.length > 0) {
          this.allClientData = items;
          this.clientSuggestions = items.map(c => c.label || c.code);
        } else {
          this.clientSuggestions = [];
          this.allClientData = [];
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        this.clientSuggestions = [];
        this.allClientData = [];
      }
    },

    // Handle Client input (when user types manually)
    onClientInput(value) {
      const matchingClient = this.allClientData.find(c => (c.label || c.code) === value);
      if (matchingClient) {
        this.selectedClientObject = matchingClient;
        this.formData.client = matchingClient.code; // Store the code when selected from suggestions
      } else {
        this.selectedClientObject = null;
        this.formData.client = value;
      }
    },

    // Handle Client selection from autocomplete dropdown
    onClientSelect(event) {
      if (event.value) {
        const matchingClient = this.allClientData.find(c => (c.label || c.code) === event.value);
        if (matchingClient) {
          this.selectedClientObject = matchingClient;
          this.formData.client = matchingClient.code; // Store the code when selected
        }
      }
    },

    // Get display value for MRP Group autocomplete
    getMrpGroupDisplayValue() {
      if (this.selectedMrpGroupObject && this.selectedMrpGroupObject.label) {
        return this.selectedMrpGroupObject.label;
      }
      return this.formData.mrp_group || '';
    },

    // Search MRP Groups for autocomplete
    async searchMrpGroups(query) {
      if (!query || query.length < 2) {
        this.mrpGroupSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('gm-mrp-groups', { search: query, limit: 100, client: this.clientCode });
        const listData = response.data && response.data.data;
        const rawItems = Array.isArray(listData) ? listData : (listData && Array.isArray(listData.data) ? listData.data : []);
        const items = Array.from(new Map(rawItems.map(mg => [mg.code, mg])).values());

        if (items.length > 0) {
          this.allMrpGroupData = items;
          this.mrpGroupSuggestions = items.map(mg => mg.label);
        } else {
          this.mrpGroupSuggestions = [];
          this.allMrpGroupData = [];
        }
      } catch (error) {
        console.error('Error searching MRP groups:', error);
        this.mrpGroupSuggestions = [];
        this.allMrpGroupData = [];
      }
    },

    // Handle MRP Group input (when user types manually)
    onMrpGroupInput(value) {
      const matchingMrpGroup = this.allMrpGroupData.find(mg => mg.label === value);
      if (matchingMrpGroup) {
        this.selectedMrpGroupObject = matchingMrpGroup;
        this.formData.mrp_group = matchingMrpGroup.code; // Store the code when selected from suggestions
      } else {
        this.selectedMrpGroupObject = null;
        this.formData.mrp_group = value;
      }
    },

    // Handle MRP Group selection from autocomplete dropdown
    onMrpGroupSelect(event) {
      if (event.value) {
        const matchingMrpGroup = this.allMrpGroupData.find(mg => mg.label === event.value);
        if (matchingMrpGroup) {
          this.selectedMrpGroupObject = matchingMrpGroup;
          this.formData.mrp_group = matchingMrpGroup.code; // Store the code when selected
        }
      }
    },

    // Get display value for MRP Type autocomplete
    getMrpTypeDisplayValue() {
      if (this.selectedMrpTypeObject && this.selectedMrpTypeObject.label) {
        return this.selectedMrpTypeObject.label;
      }
      return this.formData.mrp_type || '';
    },

    // Search MRP Types for autocomplete
    async searchMrpTypes(query) {
      if (!query || query.length < 2) {
        this.mrpTypeSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('mrp-types', { search: query, limit: 100, client: this.clientCode });
        const listData = response.data && response.data.data;
        const items = Array.isArray(listData) ? listData : (listData && Array.isArray(listData.data) ? listData.data : []);

        if (items.length > 0) {
          this.allMrpTypeData = items;
          this.mrpTypeSuggestions = items.map(mt => mt.label);
        } else {
          this.mrpTypeSuggestions = [];
          this.allMrpTypeData = [];
        }
      } catch (error) {
        console.error('Error searching MRP types:', error);
        this.mrpTypeSuggestions = [];
        this.allMrpTypeData = [];
      }
    },

    // Handle MRP Type input (when user types manually)
    onMrpTypeInput(value) {
      const matchingMrpType = this.allMrpTypeData.find(mt => mt.label === value);
      if (matchingMrpType) {
        this.selectedMrpTypeObject = matchingMrpType;
        this.formData.mrp_type = matchingMrpType.code; // Store the code when selected from suggestions
      } else {
        this.selectedMrpTypeObject = null;
        this.formData.mrp_type = value;
      }
    },

    // Handle MRP Type selection from autocomplete dropdown
    onMrpTypeSelect(event) {
      if (event.value) {
        const matchingMrpType = this.allMrpTypeData.find(mt => mt.label === event.value);
        if (matchingMrpType) {
          this.selectedMrpTypeObject = matchingMrpType;
          this.formData.mrp_type = matchingMrpType.code; // Store the code when selected
        }
      }
    },

    // Get display value for Material Freight Group autocomplete
    getMaterialFreightGroupDisplayValue() {
      if (this.selectedMaterialFreightGroupObject && this.selectedMaterialFreightGroupObject.label) {
        return this.selectedMaterialFreightGroupObject.label;
      }
      return this.formData.material_freight_group || '';
    },

    // Search Material Freight Groups for autocomplete
    async searchMaterialFreightGroups(query) {
      if (!query || query.length < 2) {
        this.materialFreightGroupSuggestions = [];
        return;
      }

      try {
        const response = await generalMasterDataService.list('gm-mat-freight-groups', { search: query, limit: 100, client: this.clientCode });
        const listData = response.data && response.data.data;
        const items = Array.isArray(listData) ? listData : (listData && Array.isArray(listData.data) ? listData.data : []);

        if (items.length > 0) {
          this.allMaterialFreightGroupData = items;
          this.materialFreightGroupSuggestions = items.map(mf => mf.label);
        } else {
          this.materialFreightGroupSuggestions = [];
          this.allMaterialFreightGroupData = [];
        }
      } catch (error) {
        console.error('Error searching material freight groups:', error);
        this.materialFreightGroupSuggestions = [];
        this.allMaterialFreightGroupData = [];
      }
    },

    // Handle Material Freight Group input (when user types manually)
    onMaterialFreightGroupInput(value) {
      const matchingMaterialFreightGroup = this.allMaterialFreightGroupData.find(mf => mf.label === value);
      if (matchingMaterialFreightGroup) {
        this.selectedMaterialFreightGroupObject = matchingMaterialFreightGroup;
        this.formData.material_freight_group = matchingMaterialFreightGroup.code; // Store the code when selected from suggestions
      } else {
        this.selectedMaterialFreightGroupObject = null;
        this.formData.material_freight_group = value;
      }
    },

    // Handle Material Freight Group selection from autocomplete dropdown
    onMaterialFreightGroupSelect(event) {
      if (event.value) {
        const matchingMaterialFreightGroup = this.allMaterialFreightGroupData.find(mf => mf.label === event.value);
        if (matchingMaterialFreightGroup) {
          this.selectedMaterialFreightGroupObject = matchingMaterialFreightGroup;
          this.formData.material_freight_group = matchingMaterialFreightGroup.code; // Store the code when selected
        }
      }
    },
  },
};
</script>

<style scoped>
/* Standardize size for inputs, autocomplete, and selects within search form */
.search-form .form-control,
.search-form .form-select {
  height: 44px;
  line-height: 1.25;
  font-size: 14px !important;
  font-family: inherit;
  padding: 8px 12px;
  box-sizing: border-box;
}

/* PrimeVue input inside AutoComplete */
.search-form :deep(.p-inputtext) {
  height: 44px;
  line-height: 1.25;
  font-size: 14px !important;
  font-family: inherit;
  padding: 8px 12px;
  width: 100%;
  box-sizing: border-box;
}

/* Ensure wrapper of AutoComplete inherits consistent font size */
.search-form :deep(.p-autocomplete) {
  font-size: 14px !important;
  font-family: inherit;
}

/* Placeholder sizing and color */
.search-form .form-control::placeholder,
.search-form :deep(.p-inputtext::placeholder) {
  font-size: 14px;
  color: #6c757d;
  opacity: 1;
}

/* Material Number field with search & clear icons */
.material-number-field {
  display: flex;
  align-items: center;
}

.material-number-field .form-control {
  padding-left: 40px;
  padding-right: 40px;
}

.material-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 16px;
  pointer-events: none;
}

.material-clear-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 14px;
  cursor: pointer;
}

.material-clear-icon:hover {
  color: #dc3545;
}

.field-group-title {
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 2px;
}

/* Form section grouping */
.form-section-title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 4px;
  margin-bottom: 12px;
}
</style>
